/* eslint-disable import/no-anonymous-default-export */
import React, { Fragment, useEffect, useState, useRef, useContext } from "react";
import WebSocket from "react-websocket";

import { _CFG } from "../../modules/config";
import { _U, _CK } from "../../modules/utils";
import { AppContext } from "../AppProvider";

const _OPTIONS = [
    { action: "BROADCAST", label: "전체" },
    { action: "IAM", label: "내 접속 ID" },
    { action: "ECHO", label: "에코" },
];

export default (props) => {
    const {iam} = useContext(AppContext);
    const { siteInfo,consoleCfg,debug, onConnected, onDisconnected, onMessage, eventIdx: propsEventIdx, sessionIdx: propsSessionIdx } = props;
    
    const urlParams = [];
    
    urlParams.push("&siteIdx=" + siteInfo?.siteIdx);
    urlParams.push("&eventIdx=" + consoleCfg?.eventIdx);
    urlParams.push("&sessionIdx=" + consoleCfg?.sessionIdx);
    urlParams.push("&consoleIdx=" + consoleCfg?.consoleIdx);
    urlParams.push("&sessionName=" + encodeURIComponent(consoleCfg?.sessionName));
    urlParams.push("&passCode=" + iam?.passCode);
    // 같은 브라우저 탭 간 dedup: 쿠키 logKey 우선, 없으면 iam.logKey 사용
    urlParams.push("&logKey=" + (_CK.getLogKey() || iam?.logKey));
    urlParams.push("&consoleCountView=" + _CK.getConsoleCountView());

    const socket = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const isConnectedRef = useRef(false); // 🔥 클로저 문제 해결을 위한 ref
    const [debugMessages, setDebugMessages] = useState([]);
    
    // 🔥 자동 재연결 및 heartbeat 관련 refs
    const heartbeatIntervalRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const isManualDisconnectRef = useRef(false);
    const [reconnectKey, setReconnectKey] = useState(0);
    const HEARTBEAT_INTERVAL = 60 * 1000; // 60초 (ALB idle timeout 3600s 회피 + 모바일 backgrounding 대비)

    // 🔥 eventIdx와 sessionIdx를 ref로 관리하여 클로저 문제 해결
    const eventIdxRef = useRef(consoleCfg?.eventIdx || propsEventIdx);
    const sessionIdxRef = useRef(consoleCfg?.sessionIdx || propsSessionIdx);
    
    // 🔥 props나 consoleCfg가 변경될 때마다 ref 업데이트
    useEffect(() => {
        eventIdxRef.current = consoleCfg?.eventIdx || propsEventIdx;
        sessionIdxRef.current = consoleCfg?.sessionIdx || propsSessionIdx;
    }, [consoleCfg?.eventIdx, consoleCfg?.sessionIdx, propsEventIdx, propsSessionIdx]);

    const sendHeartbeat = () => {
        const connected = isConnectedRef.current;
        const currentEventIdx = eventIdxRef.current;
        const currentSessionIdx = sessionIdxRef.current;

        if (connected && socket?.current) {
            if (!currentEventIdx || !currentSessionIdx) return;

            try {
                const heartbeatMessage = JSON.stringify({
                    action: 'IAM',
                    eventIdx: currentEventIdx,
                    sessionIdx: currentSessionIdx
                });
                socket.current.sendMessage(heartbeatMessage);
                addDebugMessage(":: Heartbeat sent!");
            } catch (e) {
                console.error('💓 [Socket] Failed to send heartbeat:', e);
            }
        }
    };

    const startHeartbeat = () => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
        }

        // 5분마다 heartbeat 전송 (API Gateway Idle Timeout: 10분)
        heartbeatIntervalRef.current = setInterval(() => {
            sendHeartbeat();
        }, HEARTBEAT_INTERVAL);
    };

    const stopHeartbeat = () => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }
    };

    const attemptReconnect = () => {
        reconnectAttemptsRef.current++;
        // 지수 백오프, 최대 30초 (사용자 복귀 시 자동 회복을 위해 무제한 재시도)
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);

        reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectKey(prev => prev + 1);
        }, delay);
    };

    const onConnect = () => {
        // 재연결 여부 판별(첫 연결은 reconnectAttempts=0). 리셋 전에 캡처한다.
        const wasReconnect = reconnectAttemptsRef.current > 0;
        setIsConnected(true);
        isConnectedRef.current = true;
        isManualDisconnectRef.current = false; // 자연 단절 후 재연결 시 플래그 리셋
        reconnectAttemptsRef.current = 0;
        stopHeartbeat();
        startHeartbeat();
        addDebugMessage(":: Socket Connect!");
        // [WS 끊김 진단] 내부 WebSocket의 close 이벤트(code/reason)를 로깅한다.
        // react-websocket의 onClose는 CloseEvent를 넘기지 않으므로 ws에 직접 리스너를 단다.
        // close code 분포로 원인 판별: 1000/1001=정상·이탈, 1006=비정상(네트워크), 1011=서버.
        try {
            const ws = socket.current && socket.current.state && socket.current.state.ws;
            if (ws && !ws.__closeLogged) {
                ws.__closeLogged = true;
                ws.addEventListener('close', (evt) => {
                    console.warn(`[WS-CLOSE] code=${evt.code} reason="${evt.reason || ''}" clean=${evt.wasClean} at=${new Date().toISOString()}`);
                });
            }
        } catch (e) {}
        // 재연결 시: 끊긴 동안 놓친 실시간 노출 상태(서베이/팝업)를 콘솔이 재동기화하도록 신호.
        // (WS 메시지로만 갱신되던 상태가 끊김 후 stale 되어 토글 저장이 거부되는 문제 방지)
        if (wasReconnect) {
            _U.postMessage("SOCKET-RECEIVED", JSON.stringify({ mode: "SOCKET_RECONNECTED" }));
        }
        onConnected && onConnected();
    };

    const onDisconnect = () => {
        setIsConnected(false);
        isConnectedRef.current = false; // 🔥 ref도 업데이트
        stopHeartbeat(); // heartbeat 중지
        addDebugMessage(":: Socket Disconnect!");
        onDisconnected && onDisconnected();
        
        // 🔥 의도적인 DISCONNECT가 아니면 자동 재연결 시도
        if (!isManualDisconnectRef.current) {
            attemptReconnect();
        } else {
            // 의도적인 DISCONNECT면 플래그 리셋
            isManualDisconnectRef.current = false;
            reconnectAttemptsRef.current = 0;
        }
    };

    const onMessageReceived = (data) => {
        if (data === "OK") {
            return;
        }
        addDebugMessage(data);
        onMessage && onMessage(data);
        try {
            const d = JSON.parse(data);
            const isLocal = _CFG.isLocal();
            _U.postMessage("SOCKET-RECEIVED", JSON.stringify(isLocal ? d.data : d));
        } catch (e) {}
    };

    const addDebugMessage = (message) => {
        if (debug) {
            setDebugMessages((o) => [message, ...o]);
        }
    };

    const onSendMessage = (message) => {
        try {
            // 🔧 DISCONNECT 메시지인 경우 logKey를 쿠키에서 다시 읽어서 교체
            const messageData = JSON.parse(message);
            if (messageData.action === 'DISCONNECT' || messageData.mode === 'DISCONNECT') {
                // 🔥 의도적인 DISCONNECT 플래그 설정 (자동 재연결 방지)
                isManualDisconnectRef.current = true;
                
                const currentLogKey = _CK.getLogKey();
                const currentConsoleCountView = _CK.getConsoleCountView();
                
                // data 객체가 있으면 업데이트
                if (messageData.data) {
                    messageData.data.logKey = currentLogKey;
                    messageData.data.consoleCountView = currentConsoleCountView;
                } else {
                    messageData.logKey = currentLogKey;
                    messageData.consoleCountView = currentConsoleCountView;
                }
                
                message = JSON.stringify(messageData);
            }

            socket?.current?.sendMessage(message);
        } catch (e) {
            console.error('Socket sendMessage error:', e);
        }
    };

    const onReceivedMessage = (e) => {
        _U.parseMessage(e).then((data) => {
            switch (data.code) {
                case "SOCKET-SEND":
                    onSendMessage(JSON.stringify(data.data));
                    break;
            }
        });
    };

    useEffect(() => {
        window.addEventListener("message", onReceivedMessage);
        return () => {
            window.removeEventListener("message", onReceivedMessage);
        };
    }, []);

    // 🔥 컴포넌트 언마운트 시 정리
    useEffect(() => {
        return () => {
            stopHeartbeat();
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, []);

    return (
        <Fragment>
            <WebSocket 
                key={reconnectKey} 
                ref={socket} 
                url={_CFG.wsHost() + urlParams.join("")} 
                onOpen={onConnect} 
                onClose={onDisconnect} 
                onMessage={onMessageReceived} 
                debug={debug} 
            />
            {debug && <DebugForm messages={debugMessages} onSend={onSendMessage} />}
        </Fragment>
    );
};

const DebugForm = (props) => {
    const { messages, onSend } = props;
    const [action, setAction] = useState(_OPTIONS[0].action);
    const [message, setMessage] = useState("");
    const onSendClick = () => {
        const form = { action, data: { mode: "NOTICE", message } };
        onSend && onSend(JSON.stringify(form));
        setMessage("");
    };
    return (
        <Fragment>
            <div className="row">
                <div className="col-md-4">
                    <select value={action} onChange={(e) => setAction(e.target.value)} className="form-control">
                        {_OPTIONS.map((item, i) => (
                            <option key={i} value={item.action}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-8">
                    <div className="input-group">
                        <input type="text" value={message} onChange={(e) => setMessage(e.target.value)} className="form-control" />
                        <div className="input-group-append">
                            <button type="button" className="btn btn-primary" onClick={onSendClick}>
                                <i className="fa fa-send"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <textarea className="form-control" readOnly value={messages?.join("\n")} rows={10} />
                </div>
            </div>
        </Fragment>
    );
};
